import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import { 
  ExclamationCircleIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  XCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const DomainSection = ({ user }) => {
  const { darkMode } = useTheme();
  const [domainStatus, setDomainStatus] = useState('checking');
  const [domainName, setDomainName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [domainInfo, setDomainInfo] = useState(null);
  const [organizationInfo, setOrganizationInfo] = useState(null);
  const [showVerificationHelp, setShowVerificationHelp] = useState(false);
  const [lookupAttempted, setLookupAttempted] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('dns-txt');
  const [isSubmittingDomain, setIsSubmittingDomain] = useState(false);
  const [domain, setDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [customDomain, setCustomDomain] = useState(null);
  const [verificationDetails, setVerificationDetails] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [dnsCheckResults, setDnsCheckResults] = useState(null);
  const [checkingDns, setCheckingDns] = useState(false);
  const [showDomainForm, setShowDomainForm] = useState(false);
  const [cnameCheckResults, setCnameCheckResults] = useState(null);
  const [checkingCname, setCheckingCname] = useState(false);
  // Track scroll position
  const [scrollPosition, setScrollPosition] = useState(0);
  // Track if we've completed initial status checks
  const [initialCheckComplete, setInitialCheckComplete] = useState(false);
  // For active/verified domains, assume everything is good until checked
  const [forceCollapsedView, setForceCollapsedView] = useState(true);
  
  // Refs for scrolling
  const deleteConfirmRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const scrollbarStyle = {
    maxHeight: 'calc(100vh - 120px)',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    msOverflowStyle: 'none',  /* IE and Edge */
    scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
    paddingBottom: '24px',
    paddingRight: '15px'
  };

  // Simplified scrollbar styling without hover states
  const scrollbarStylesWebkit = `
    .domain-section::-webkit-scrollbar {
      width: 8px;
    }
    .domain-section::-webkit-scrollbar-track {
      background: transparent;
    }
    .domain-section::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.2);
      border-radius: 20px;
    }
    
    .dark .domain-section::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.1);
      border-radius: 20px;
    }
    
    /* Override browser autofill styling */
    /* Light mode */
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px white inset !important;
      -webkit-text-fill-color: black !important;
      transition: background-color 5000s ease-in-out 0s;
      box-shadow: 0 0 0 30px white inset !important;
    }
    
    /* Dark mode */
    .dark input:-webkit-autofill,
    .dark input:-webkit-autofill:hover,
    .dark input:-webkit-autofill:focus,
    .dark input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px #0A0A0A inset !important;
      -webkit-text-fill-color: white !important;
      transition: background-color 5000s ease-in-out 0s;
      box-shadow: 0 0 0 30px #0A0A0A inset !important;
    }
    
    @-moz-document url-prefix() {
      input:-webkit-autofill {
        background-color: white !important;
      }
      
      .dark input:-webkit-autofill {
        background-color: #0A0A0A !important;
      }
    }
  `;

  // Toast style based on dark mode
  const toastStyle = {
    style: {
      background: darkMode ? '#333' : '#fff',
      color: darkMode ? '#fff' : '#000',
      border: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
    },
    success: {
      style: {
        background: darkMode ? '#0c4a2d' : '#ecfdf5',
        color: darkMode ? '#fff' : '#065f46',
        border: `1px solid ${darkMode ? '#065f46' : '#d1fae5'}`,
      },
    },
    error: {
      style: {
        background: darkMode ? '#4c1d24' : '#fef2f2',
        color: darkMode ? '#fff' : '#b91c1c',
        border: `1px solid ${darkMode ? '#b91c1c' : '#fee2e2'}`,
      },
    },
    loading: {
      style: {
        background: darkMode ? '#1e3a8a' : '#eff6ff',
        color: darkMode ? '#fff' : '#1e40af',
        border: `1px solid ${darkMode ? '#1e40af' : '#dbeafe'}`,
      },
    },
  };

  // Replace the fetchDomainInfo function with a refresh function that uses our new approach
  const refreshDomainInfo = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      // First get organization info to check if admin and what organization they belong to
      const orgResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/organization`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      let isOrganizationAdmin = false;
      
      if (orgResponse.data.hasOrganization) {
        const orgRole = orgResponse.data.organization.userRole;
        isOrganizationAdmin = orgRole === 'admin' || orgRole === 'owner';
        setOrganizationInfo(orgResponse.data.organization);
      }
      
      // If this user is an organization admin, get the organization's domain info
      if (isOrganizationAdmin) {
        // Get organization's domain info
        const companyResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/organization/domain`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (companyResponse.data && companyResponse.data.customDomain) {
          // Skip auto-generated internal domains that contain "company-"
          const domainToCheck = companyResponse.data.customDomain.domain || '';
          if (domainToCheck.includes("company-")) {
            // This is an internal domain, don't show it to the user
            setDomainStatus('not-set');
            setLookupAttempted(true);
            setCustomDomain(null);
            setVerificationDetails(null);
          } else {
            // This is a proper custom domain, display it
            setDomainInfo(companyResponse.data.customDomain);
            setDomainStatus(companyResponse.data.customDomain.status);
            setLookupAttempted(true);
            
            // Set other required state
            setCustomDomain(companyResponse.data.customDomain);
            
            // Only fetch verification details if domain is pending
            if (companyResponse.data.customDomain.status === 'pending') {
              // Check if we already have verification token from response
              if (companyResponse.data.customDomain.verificationToken) {
                setVerificationDetails({
                  recordType: 'TXT',
                  recordName: `_midus-verify.${companyResponse.data.customDomain.domain}`,
                  token: companyResponse.data.customDomain.verificationToken
                });
              } else {
                // We don't have verification token, need to register again
                setVerificationDetails(null);
              }
            }
          }
        } else {
          // Organization doesn't have domain set up
          setDomainStatus('not-set');
          setLookupAttempted(true);
          setCustomDomain(null);
          setVerificationDetails(null);
        }
      } else {
        // Not an org admin, check personal domain
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/domains`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data && response.data.customDomain) {
          // Skip auto-generated internal domains that contain "company-"
          const domainToCheck = response.data.customDomain.domain || '';
          if (domainToCheck.includes("company-")) {
            // This is an internal domain, don't show it to the user
            setDomainStatus('not-set');
            setLookupAttempted(true);
            setCustomDomain(null);
            setVerificationDetails(null);
          } else {
            // This is a proper custom domain, display it
            setDomainInfo(response.data.customDomain);
            setDomainStatus(response.data.customDomain.status);
            setLookupAttempted(true);
            
            // Set other required state
            setCustomDomain(response.data.customDomain);
            
            // Only fetch verification details if domain is pending
            if (response.data.customDomain.status === 'pending') {
              // Check if we already have verification token from response
              if (response.data.customDomain.verificationToken) {
                setVerificationDetails({
                  recordType: 'TXT',
                  recordName: `_midus-verify.${response.data.customDomain.domain}`,
                  token: response.data.customDomain.verificationToken
                });
              } else {
                // We don't have verification token, need to register again
                setVerificationDetails(null);
              }
            }
          }
        } else {
          setDomainStatus('not-set');
          setLookupAttempted(true);
          setCustomDomain(null);
          setVerificationDetails(null);
        }
      }
    } catch (error) {
      console.error('Error refreshing domain info:', error);
      setDomainStatus('not-set');
      setLookupAttempted(true);
      
      if (error.response?.status === 404) {
        // This is normal for a new user, don't show error
        console.log('No domain found (404)');
      } else {
        toast.error('Failed to load domain information');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle initial domain state
  useEffect(() => {
    if (customDomain && (customDomain.status === 'verified' || customDomain.status === 'active')) {
      // Immediately set the collapsed view to prevent flashing
      setForceCollapsedView(true);
      
      // Start the initial check process if not already completed
      if (!initialCheckComplete) {
        handleCheckCname(true);
      }
    } else {
      // For other statuses, don't force the collapsed view
      setForceCollapsedView(false);
    }
  }, [customDomain, initialCheckComplete]);

  // Add a useEffect for auto-refreshing domain status when in 'verified' status
  useEffect(() => {
    // Only auto-refresh when the domain is in 'verified' status (waiting for SSL)
    if (customDomain && customDomain.status === 'verified') {
      // Do an initial CNAME check if we haven't done it yet and we've completed the initial check
      if (initialCheckComplete && !cnameCheckResults) {
      handleCheckCname();
      }
      
      // Set up a refresh interval - every 30 seconds
      const intervalId = setInterval(() => {
        // Only check if we haven't already confirmed SSL is valid
        if (!cnameCheckResults?.ssl?.valid) {
        handleCheckCname();
        
        // Refresh domain info every 2 minutes to check for status updates
        if (Date.now() % (2 * 60 * 1000) < 30000) {
          refreshDomainInfo();
          }
        } else {
          // Clear the interval if SSL is valid
          clearInterval(intervalId);
        }
      }, 30000);
      
      // Clear the interval when the component unmounts or status changes
      return () => clearInterval(intervalId);
    }
  }, [customDomain?.status, cnameCheckResults?.ssl?.valid, initialCheckComplete]);

  // Effect to scroll to delete confirmation when it appears
  useEffect(() => {
    if (showDeleteConfirm && deleteConfirmRef.current) {
      // Add a small delay to ensure the element is rendered
      setTimeout(() => {
        if (deleteConfirmRef.current) {
          // Scroll the element into view with a smooth behavior
          // Use a scrollIntoView option that's less disruptive
          deleteConfirmRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest' 
          });
          
          // Remove animation classes that could cause glitchy effects
          // deleteConfirmRef.current.classList.add('ring-4', 'ring-red-300', 'dark:ring-red-800');
          // deleteConfirmRef.current.classList.add('animate-pulse');
          // setTimeout(() => {
          //   if (deleteConfirmRef.current) {
          //     deleteConfirmRef.current.classList.remove('animate-pulse');
          //   }
          // }, 1000);
        }
      }, 100);
    }
  }, [showDeleteConfirm]);

  // Add useEffect to load domain info on component mount
  useEffect(() => {
    refreshDomainInfo();
  }, []);

  const validateDomain = (value) => {
    // Basic domain validation regex
    const domainRegex = /^(?!:\/\/)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    
    if (!value) {
      return 'Domain name is required';
    }
    if (!domainRegex.test(value)) {
      return 'Please enter a valid domain name (e.g., yourdomain.com)';
    }
    
    return '';
  };

  const handleDomainChange = (e) => {
    const value = e.target.value;
    setDomain(value);
    setDomainError(validateDomain(value));
  };

  const handleAddDomain = () => {
    setShowDomainForm(true);
    setDomainError('');
    setDomain('');
  };

  const handleDomainSubmit = async (e) => {
    e.preventDefault();
    
    const error = validateDomain(domain);
    if (error) {
      setDomainError(error);
      return;
    }
    
    try {
      setIsLoading(true);
      setVerificationError('');
      const token = localStorage.getItem('token');
      
      // Determine if we should use the organization domain API or the regular domain API
      const isOrgAdmin = organizationInfo && 
        (organizationInfo.userRole === 'admin' || organizationInfo.userRole === 'owner');
      
      const endpoint = isOrgAdmin 
        ? `${process.env.REACT_APP_API_URL}/organization/domain` 
        : `${process.env.REACT_APP_API_URL}/domains`;
      
      const response = await axios.post(
        endpoint,
        { domain },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Store the domain object 
      if (isOrgAdmin) {
        setCustomDomain(response.data.customDomain);
      } else {
        setCustomDomain(response.data.domain);
      }
      
      // Store verification details
      if (response.data.verificationDetails) {
        setVerificationDetails(response.data.verificationDetails);
      }
      
      // Hide the form after successful submission
      setShowDomainForm(false);
      setDomain('');
    } catch (error) {
      console.error('Error registering domain:', error);
      if (error.response?.status === 409) {
        toast.error('This domain is already registered', toastStyle.error);
      } else {
        toast.error('Failed to register domain', toastStyle.error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDomainAdd = () => {
    setShowDomainForm(false);
    setDomainError('');
    setDomain('');
  };

  const handleVerifyDomain = async () => {
    try {
      setVerifyingDomain(true);
      setVerificationError('');
      const token = localStorage.getItem('token');
      
      // Determine if we should use the organization domain API or the regular domain API
      const isOrgAdmin = organizationInfo && 
        (organizationInfo.userRole === 'admin' || organizationInfo.userRole === 'owner');
      
      const endpoint = isOrgAdmin 
        ? `${process.env.REACT_APP_API_URL}/organization/domain/verify` 
        : `${process.env.REACT_APP_API_URL}/domains/verify`;
      
      const response = await axios.post(
        endpoint,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Domain verified successfully', toastStyle.success);
      await refreshDomainInfo();
    } catch (error) {
      console.error('Error verifying domain:', error);
      
      let errorMsg = 'Failed to verify domain';
      if (error.response?.data?.details) {
        errorMsg = error.response.data.details;
      }
      
      setVerificationError(errorMsg);
    } finally {
      setVerifyingDomain(false);
    }
  };

  const handleDeleteClick = () => {
    // Save current scroll position before showing confirmation
    if (scrollContainerRef.current) {
      setScrollPosition(scrollContainerRef.current.scrollTop);
    }
    setShowDeleteConfirm(true);
  };

  const handleDeleteDomain = async () => {
    try {
      // Immediately reset all UI elements to prevent flashing
      setIsLoading(true);
      setShowDeleteConfirm(false);
      
      // Immediately hide all badges and reset all domain-related state
      setForceCollapsedView(false);
      setCustomDomain(null);
      setVerificationDetails(null);
      setCnameCheckResults(null);
      setDnsCheckResults(null);
      setInitialCheckComplete(true);
      setShowDomainForm(false);
      setDomain('');
      setDomainError('');
      
      // Determine if we should use the organization domain API or the regular domain API
      const isOrgAdmin = organizationInfo && 
        (organizationInfo.userRole === 'admin' || organizationInfo.userRole === 'owner');
      
      const endpoint = isOrgAdmin 
        ? `${process.env.REACT_APP_API_URL}/organization/domain` 
        : `${process.env.REACT_APP_API_URL}/domains`;
      
      // Now make the API call in the background
      const token = localStorage.getItem('token');
      await axios.delete(
        endpoint,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // No longer need to re-fetch or use setTimeout, which causes the jump
      // Just keep all states reset as we already did above
    } catch (error) {
      console.error('Error removing domain:', error);
      // Keep the error toast as a fallback for API failures
      toast.error('Failed to remove custom domain', toastStyle.error);
      
      // If there's an error, try a clean fetch but don't use setTimeout
      refreshDomainInfo();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckDns = async () => {
    if (!customDomain || !customDomain.domain) return;
    
    try {
      setCheckingDns(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/domains/check-dns/${customDomain.domain}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setDnsCheckResults(response.data.results);
    } catch (error) {
      console.error('Error checking DNS:', error);
    } finally {
      setCheckingDns(false);
    }
  };

  const handleCheckCname = async (isInitialCheck = false) => {
    if (!customDomain || !customDomain.domain) return;
    
    try {
      setCheckingCname(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/domains/check-cname/${customDomain.domain}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setCnameCheckResults(response.data.results);
      
      // If this was an initial check, mark it as complete
      if (isInitialCheck) {
        setInitialCheckComplete(true);
        
        // Update forceCollapsedView based on the check results
        if (response.data.results.cnameValid && 
            response.data.results.ssl && 
            response.data.results.ssl.valid) {
          // Keep collapsed view for fully configured domains
          setForceCollapsedView(true);
        } else {
          // Domain needs configuration, show detailed view
          setForceCollapsedView(false);
        }
      }
    } catch (error) {
      console.error('Error checking CNAME and SSL:', error);
      
      // Even if there's an error during initial check, mark it as complete
      if (isInitialCheck) {
        setInitialCheckComplete(true);
        setForceCollapsedView(false); // Show detailed view on error
      }
    } finally {
      setCheckingCname(false);
    }
  };

  const getStatusBadge = () => {
    if (!customDomain) return null;
    
    switch (customDomain.status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-300 dark:text-yellow-700">
            <ClockIcon className="w-4 h-4 mr-1" />
            Verification Pending
          </span>
        );
      case 'verified':
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 border border-green-200 dark:border-green-700">
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            Verified
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100 border border-red-200 dark:border-red-700">
            <XCircleIcon className="w-4 h-4 mr-1" />
            Error
          </span>
        );
      default:
        return null;
    }
  };

  const getCnameBadge = () => {
    if (!cnameCheckResults || !cnameCheckResults.cnameValid) {
      return null;
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
        <CheckCircleIcon className="w-4 h-4 mr-1" />
        CNAME Verified
      </span>
    );
  };

  const getSslBadge = () => {
    if (!cnameCheckResults || !cnameCheckResults.ssl || !cnameCheckResults.ssl.valid) {
      return null;
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
        <CheckCircleIcon className="w-4 h-4 mr-1" />
        SSL Configured
      </span>
    );
  };

  const isFullyConfigured = () => {
    return (
      customDomain && 
      (customDomain.status === 'active' || customDomain.status === 'verified') &&
      cnameCheckResults && 
      cnameCheckResults.cnameValid && 
      cnameCheckResults.ssl && 
      cnameCheckResults.ssl.valid
    );
  };

  // Function to determine if we should show detailed configuration UI
  const shouldShowDetailedConfig = () => {
    // Never show detailed config while loading
    if (isLoading) {
      return false;
    }
    
    // If we're forcing collapsed view (regardless of initial check status), don't show detailed config
    if (forceCollapsedView) {
      return false;
    }
    
    // If there's no domain, should show detailed is false (we'll show "add domain" UI instead)
    if (!customDomain) {
      return false;
    }
    
    // Otherwise, check if domain is fully configured, but only if initial check is complete
    if (initialCheckComplete) {
      return !isFullyConfigured();
    }
    
    // If initial check not complete, but domain is verified/active, keep collapsed view
    if (!initialCheckComplete && (customDomain?.status === 'active' || customDomain?.status === 'verified')) {
      return false;
    }
    
    // For other cases (like pending), show detailed view
    return true;
  };

  // Handle Cancel Delete - restore scroll position
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    // Restore the scroll position after a brief delay
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollPosition;
      }
    }, 50);
  };

  // Render the appropriate UI based on the current domain status
  const renderDomainStatus = () => {
    // Guard clause to ensure we have a valid domain
    if (!customDomain || !customDomain.domain || !customDomain.status) {
      return null;
    }

    // Check if domain is fully configured
    const fullyConfigured = isFullyConfigured();
    const showDetailed = shouldShowDetailedConfig();
    
    // Only show CNAME and SSL badges if properly verified or during initial loading
    const showCnameBadge = (cnameCheckResults?.cnameValid === true) || (forceCollapsedView && !initialCheckComplete);
    const showSslBadge = (cnameCheckResults?.ssl?.valid === true) || (forceCollapsedView && !initialCheckComplete);

    return (
      <div className="p-4 bg-white rounded-2xl border border-gray-200 dark:bg-[#111111] dark:border-[#444444]">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Your Custom Domain</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {customDomain.domain}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {getStatusBadge()}
              
              {/* Only show badges if verified or loading */}
              {showCnameBadge && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 border border-green-200 dark:border-green-700">
                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                  CNAME Verified
                </span>
              )}
              
              {showSslBadge && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 border border-green-200 dark:border-green-700">
                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                  SSL Configured
                </span>
              )}
              
              {/* Show loading indicator when doing initial check */}
              {!initialCheckComplete && (customDomain.status === 'active' || customDomain.status === 'verified') && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700">
                  <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
                  Checking...
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="account-modal-btn p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 rounded-lg"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div 
            ref={deleteConfirmRef}
            className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-300 dark:border-red-700"
          >
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-400">Confirm Deletion</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>Are you sure you want to remove your custom domain? This action cannot be undone.</p>
                </div>
                <div className="mt-4 flex space-x-4">
                  <button
                    type="button"
                    className="account-modal-btn inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-lg text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none dark:text-red-300 dark:bg-red-900 dark:hover:bg-red-800"
                    onClick={handleDeleteDomain}
                  >
                    Yes, Delete
                  </button>
                  <button
                    type="button"
                    className="account-modal-btn inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none dark:text-gray-300 dark:bg-[#111111] dark:border-[#222222] dark:hover:bg-[#111111]"
                    onClick={handleCancelDelete}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* If domain is configured or in forced collapsed view, show the URL */}
        {(!showDetailed || forceCollapsedView) && !showDeleteConfirm && isFullyConfigured() && (
          <div className="mt-5">
            <a 
              href={`https://ai.${customDomain.domain}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="account-modal-btn inline-flex items-center px-4 py-2 border rounded-lg shadow-sm text-sm font-medium text-black bg-white hover:bg-gray-50 dark:bg-[#111111] dark:text-white dark:border-[#444444] dark:hover:bg-[#111111]"
            >
              <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-2" />
              https://ai.{customDomain.domain}
            </a>
          </div>
        )}

        {/* Show CNAME and SSL setup instructions for verified domains that aren't fully configured */}
        {(customDomain.status === 'verified' || customDomain.status === 'active') && initialCheckComplete && !isFullyConfigured() && !showDeleteConfirm && (
          <>
            {/* Missing CNAME */}
            {!cnameCheckResults?.cnameValid && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">Next Steps</h4>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Your domain has been verified. Now, add the following CNAME record to point your subdomain to our servers:
                </p>
                
                <div className="mt-2 overflow-x-auto">
                  <table className="min-w-full border border-gray-200 dark:border-[#444444] rounded-xl overflow-hidden">
                    <thead className="bg-gray-200 dark:bg-[#333333]">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Record Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Value</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-100 dark:bg-[#222222]">
                      <tr>
                        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">CNAME</td>
                        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">ai</td>
                        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 font-mono">midus.co</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-4 p-3 bg-white dark:bg-[#111111] dark:border border dark:border-[#444444] rounded-xl">
                  <h5 className="text-sm font-medium text-black dark:text-white mb-2">CNAME Setup Instructions</h5>
                  
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    <strong>For GoDaddy Users:</strong>
                    <ul className="list-disc ml-5 mt-1">
                      <li>Enter only <code className="bg-gray-200 dark:bg-[#222222] py-0.3 px-1 rounded">ai</code> in the "Name" or "Host" field</li>
                      <li>GoDaddy will create <code className="bg-gray-200 dark:bg-[#222222] py-0.3 px-1 rounded">ai.{customDomain.domain}</code></li>
                      <li>Set the Value/Points to: <code className="bg-gray-200 dark:bg-[#222222] py-0.3 px-1 rounded">midus.co</code></li>
                    </ul>
                  </div>
                  
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    <strong>After setting up the CNAME record:</strong>
                    <ul className="list-disc ml-5 mt-1">
                      <li>Your application will be accessible at <code className="bg-gray-200 dark:bg-[#222222] py-0.3 px-1 rounded">ai.{customDomain.domain}</code></li>
                      <li>DNS changes can take 30 minutes to 48 hours to propagate</li>
                      <li>We'll provision an SSL certificate for your subdomain automatically</li>
                    </ul>
                  </div>
                </div>
                
                {/* CNAME check results if available */}
                {cnameCheckResults && (
                  <div className="mt-4 p-3 bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#444444]">
                    <div className="flex items-center mb-2">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white mr-2">
                        Subdomain Configuration Status
                      </h5>
                      <ArrowPathIcon className={`w-4 h-4 text-gray-500 dark:text-gray-400 ${checkingCname ? 'animate-spin' : ''}`} />
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">CNAME Record: </span>
                        {cnameCheckResults.cnameRecord ? (
                          <span className="font-medium text-red-600 dark:text-red-400">
                            {cnameCheckResults.cnameRecord} (Invalid)
                          </span>
                        ) : (
                          <span className="text-red-600 dark:text-red-400">Not found</span>
                        )}
                      </div>
                      
                      <button
                        onClick={handleCheckCname}
                        disabled={checkingCname}
                        className="account-modal-btn mt-2 inline-flex items-center px-3 py-2 border border-black text-sm rounded-lg shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none dark:bg-white dark:text-black dark:border-white dark:hover:bg-gray-200"
                      >
                        {checkingCname ? (
                          <>
                            <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                            Checking...
                          </>
                        ) : (
                          'Check CNAME Configuration'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* CNAME valid but SSL not configured */}
            {cnameCheckResults?.cnameValid && (!cnameCheckResults.ssl || !cnameCheckResults.ssl.valid) && 
             cnameCheckResults?.ssl?.status !== 'found' && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">SSL Certificate Setup</h4>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Your CNAME record is configured correctly. We're now setting up SSL for your domain.
                </p>
                
                <div className="mt-4 p-4 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#444444] rounded-xl">
                  <div className="flex items-center mb-3">
                    <ArrowPathIcon className="w-5 h-5 text-black dark:text-white animate-spin mr-2" />
                    <h6 className="text-sm font-medium text-gray-900 dark:text-white">
                      SSL Certificate Provisioning
                    </h6>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-[#333333] rounded-full h-2.5 mb-3">
                    <div 
                      className="bg-black dark:bg-white h-2.5 rounded-full animate-pulse" 
                      style={{ 
                        width: '60%', 
                        animationDuration: '2s' 
                      }}
                    ></div>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    We're setting up the SSL certificate for your domain. This process typically takes 5-15 minutes.
                    <br />
                    The page will automatically refresh when your domain is ready.
                  </p>
                  
                  {/* <button
                    onClick={handleCheckCname}
                    disabled={checkingCname}
                    className="mt-3 inline-flex items-center px-3 py-2 border border-black text-sm rounded-lg shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none dark:bg-white dark:text-black dark:border-white dark:hover:bg-gray-200"
                  >
                    {checkingCname ? (
                      <>
                        <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      'Check SSL Status'
                    )}
                  </button> */}
                </div>
              </div>
            )}

            {/* Show SSL success message if CNAME is valid and we have confirmation SSL is being configured */}
            {cnameCheckResults?.cnameValid && cnameCheckResults?.ssl?.status === 'found' && !cnameCheckResults?.ssl?.valid && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">SSL Certificate Status</h4>
                <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ClockIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        SSL certificate has been detected but is still finalizing. This should be complete within a few minutes.
                      </p>
                      <button
                        onClick={handleCheckCname}
                        disabled={checkingCname}
                        className="account-modal-btn mt-2 inline-flex items-center px-3 py-1 border border-black text-xs rounded-lg shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none dark:bg-white dark:text-black dark:border-white dark:hover:bg-gray-200"
                      >
                        {checkingCname ? (
                          <>
                            <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
                            Checking...
                          </>
                        ) : (
                          'Check Again'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Only show domain verification for pending status */}
        {customDomain.status === 'pending' && !showDeleteConfirm && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Verify Your Domain</h4>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Add the following TXT record to your domain's DNS settings:
            </p>
            
            {/* Only show verification table if we have verification details */}
            {verificationDetails && verificationDetails.recordName && verificationDetails.token && (
              <div>
                <div className="mt-2 overflow-x-auto">
                  <table className="min-w-full overflow-hidden rounded-xl border border-gray-200 dark:border-[#444444]">
                    <thead className="bg-gray-200 dark:bg-[#333333]">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Record Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Value</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-100 dark:bg-[#222222]">
                      <tr>
                        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{verificationDetails.recordType || 'TXT'}</td>
                        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{verificationDetails.recordName}</td>
                        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 font-mono select-all cursor-pointer">{verificationDetails.token}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-4 p-3 bg-white dark:bg-[#111111] dark:border border dark:border-[#444444] rounded-xl">
                  <h5 className="text-sm font-medium text-black dark:text-white mb-2">DNS Provider Instructions</h5>
                  
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    <strong>For GoDaddy Users:</strong>
                    <ul className="list-disc ml-5 mt-1">
                      <li>Enter only <code className="bg-gray-200 dark:bg-[#222222] py-0.3 px-1 rounded">_midus-verify</code> in the "Name" or "Host" field (without your domain)</li>
                      <li>GoDaddy automatically appends your domain to the record name</li>
                      <li>Set the TXT Value to the token shown above</li>
                    </ul>
                  </div>
                  
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    <strong>General Instructions:</strong>
                    <ul className="list-disc ml-5 mt-1">
                      <li>DNS changes can take 30 minutes to 48 hours to propagate</li>
                      <li>Make sure there are no extra spaces in the token value</li>
                      <li>Some providers require you to add the full subdomain, others just the prefix</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {verificationError && (
              <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded">
                <p className="text-sm text-red-700 dark:text-red-400">
                  {verificationError}
                </p>
              </div>
            )}
            
            {dnsCheckResults && (
              <div className="mt-3 p-3 bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#444444]">
                <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">DNS Check Results</h5>
                
                <div className="space-y-2 text-xs">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Status: </span>
                    <span className={`font-medium ${
                      dnsCheckResults.status === 'found' 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {dnsCheckResults.status === 'found' ? 'Records Found' : 'No Records Found'}
                    </span>
                  </div>
                  
                  {dnsCheckResults.status === 'found' && (
                    <>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Records Found: </span>
                        <span className="font-medium text-gray-900 dark:text-white">{dnsCheckResults.records.length}</span>
                      </div>
                      
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 mb-1">Values:</span>
                        <ul className="list-disc ml-4 space-y-1">
                          {dnsCheckResults.records.map((record, index) => (
                            <li key={index} className="text-gray-900 dark:text-white font-mono">
                              {record.join('')}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {dnsCheckResults.hasOwnProperty('matched') && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Matches Expected Token: </span>
                          <span className={`font-medium ${
                            dnsCheckResults.matched 
                              ? 'text-green-600 dark:text-green-400' 
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {dnsCheckResults.matched ? 'Yes' : 'No'}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  
                  {dnsCheckResults.error && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Error: </span>
                      <span className="text-red-600 dark:text-red-400">{dnsCheckResults.error}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                onClick={handleVerifyDomain}
                disabled={verifyingDomain}
                className="account-modal-btn inline-flex items-center px-4 py-2 border border-black text-sm font-medium rounded-lg shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none dark:bg-white dark:text-black dark:border-white dark:hover:bg-gray-200"
              >
                {verifyingDomain ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Domain'
                )}
              </button>
              
              <button
                onClick={handleCheckDns}
                disabled={checkingDns}
                className="account-modal-btn inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none dark:text-gray-300 dark:bg-[#111111] dark:border-[#444444] dark:hover:bg-[#111111]"
              >
                {checkingDns ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                    Checking DNS...
                  </>
                ) : (
                  'Check DNS Records'
                )}
              </button>
              
              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                <InformationCircleIcon className="w-4 h-4 mr-1" />
                DNS changes may take up to 24 hours to propagate
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto">
      <style>{scrollbarStylesWebkit}</style>
      {(!customDomain || !customDomain.domain) && !showDomainForm && !isLoading && (
        <div className="pt-4 pr-4 mb-4">
          <button
            type="button"
            onClick={handleAddDomain}
            className="account-modal-btn inline-flex items-center px-4 py-2 border rounded-lg shadow-sm text-sm font-medium text-white bg-black dark:bg-white dark:text-black hover:bg-gray-800 focus:outline-none border-black dark:border-white dark:hover:bg-gray-200"
          >
            Add Domain
          </button>
        </div>
      )}
      
      <div ref={scrollContainerRef} className="domain-section" style={scrollbarStyle}>
      {/* Domain Form - shown only when explicitly adding a domain */}
        {showDomainForm && !isLoading && (
        <div className="pt-4">
          <form onSubmit={handleDomainSubmit} className="space-y-4">
            <div>
                <label htmlFor="domain" className="block pb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Domain Name
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="text"
                  name="domain"
                  id="domain"
                  value={domain}
                  onChange={handleDomainChange}
                    className={`block w-full p-3 pr-10 focus:outline-none sm:text-sm rounded-xl
                    ${domainError 
                        ? ' border border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 dark:border-red-700 dark:text-red-400 dark:bg-[#111111] dark:focus:ring-red-500 dark:focus:border-red-500'
                        : ' border border-gray-300 dark:border-[#444444] focus:ring-black focus:border-black dark:focus:ring-white dark:focus:border-white dark:bg-[#111111] dark:text-white'
                    }`}
                  placeholder="yourdomain.com"
                  aria-invalid={domainError ? "true" : "false"}
                  aria-describedby={domainError ? "domain-error" : undefined}
                />
                {domainError && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                  </div>
                )}
              </div>
              {domainError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400" id="domain-error">
                  {domainError}
                </p>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={isLoading || !!domainError}
                  className={`account-modal-btn inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg shadow-sm 
                  ${isLoading || !!domainError
                      ? 'bg-gray-300 dark:bg-[#333333] cursor-not-allowed border-gray-300 dark:border-[#444444] text-gray-500 dark:text-gray-400'
                      : 'bg-black dark:bg-white border-black text-white dark:text-black dark:border-white hover:bg-gray-800 dark:hover:bg-gray-200 focus:outline-none'
                  }`}
              >
                {isLoading ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  'Register Domain'
                )}
              </button>
              <button
                type="button"
                onClick={handleCancelDomainAdd}
                  className="account-modal-btn inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none dark:bg-[#111111] dark:text-gray-200 dark:border-[#222222] dark:hover:bg-[#111111]"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Domain Status - only shown when a valid domain exists */}
        {customDomain && customDomain.domain && customDomain.status && (
          <div className="pt-4">
            {renderDomainStatus()}
          </div>
        )}
        
        {/* Show loading state */}
        {isLoading && (
          <div className="pt-4 space-y-6">
            <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
              {/* Domain card skeleton */}
              <div className="p-4 bg-white rounded-2xl border border-gray-200 dark:bg-[#111111] dark:border-[#444444]">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <Skeleton height={16} width={120} className="mb-2" />
                    <Skeleton height={14} width={200} className="mb-3" />
                    <div className="flex flex-wrap gap-2">
                      <Skeleton height={24} width={120} />
                      <Skeleton height={24} width={100} />
                      <Skeleton height={24} width={110} />
                    </div>
                  </div>
                  <Skeleton height={20} width={20} />
                </div>
              </div>
              
              {/* Configuration steps skeleton */}
              <div className="space-y-4">
                <div className="p-4 bg-white rounded-xl border border-gray-200 dark:bg-[#111111] dark:border-[#444444]">
                  <Skeleton height={16} width={150} className="mb-2" />
                  <Skeleton height={14} width={300} className="mb-4" />
                  
                  {/* Table skeleton */}
                  <div className="border border-gray-200 dark:border-[#444444] rounded-xl overflow-hidden">
                    <div className="bg-gray-200 dark:bg-[#333333] p-3">
                      <div className="flex justify-between">
                        <Skeleton height={12} width={80} />
                        <Skeleton height={12} width={60} />
                        <Skeleton height={12} width={100} />
                      </div>
                    </div>
                    <div className="bg-gray-100 dark:bg-[#222222] p-3">
                      <div className="flex justify-between">
                        <Skeleton height={14} width={40} />
                        <Skeleton height={14} width={80} />
                        <Skeleton height={14} width={120} />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex space-x-3">
                    <Skeleton height={36} width={120} />
                    <Skeleton height={36} width={100} />
                  </div>
                </div>
              </div>
            </SkeletonTheme>
          </div>
        )}
      
      {/* No domain message - shown when no domain and no form */}
      {!customDomain && !showDomainForm && !isLoading && (
          <div className="mt-6 p-4 bg-white rounded-xl border border-gray-200 dark:bg-[#111111] dark:border-[#444444]">
          <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-[gray-400]">
              You haven't set up a custom domain yet. Click "Add Domain" to get started.
            </p>
          </div>
        </div>
      )}
      
        {/* Information section - only shown when no domain exists (and not loading) or domain is not fully configured (and initial check complete) */}
        {((!customDomain && !isLoading) || (shouldShowDetailedConfig() && !forceCollapsedView && !isLoading)) && (
          <div className="mt-8 rounded-2xl border border-gray-200 dark:border-[#444444] bg-white dark:bg-[#111111] p-4">
        <div className="flex">
          <div className="flex-shrink-0">
                <InformationCircleIcon className="h-5 w-5 text-black dark:text-white" aria-hidden="true" />
          </div>
          <div className="ml-3">
                <h3 className="text-sm font-medium text-black dark:text-white">Custom Domain Setup</h3>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              <p>
                To set up your custom domain:
              </p>
              <ol className="list-decimal ml-5 mt-1 space-y-1">
                <li>Register your domain with a domain registrar</li>
                <li>Enter your domain name and click "Register Domain"</li>
                <li>Add the required DNS records to verify your domain ownership</li>
                <li>Once verified, add CNAME record to point your subdomain to our servers</li>
                <li>Wait for SSL certificate to be provisioned (up to 24 hours)</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
        )}
        
        {/* Add empty div with padding to ensure all content is visible when scrolling */}
        <div className="h-16"></div>
            </div>
    </div>
  );
};

export default DomainSection; 